import { CreateCmpInvoiceInput } from "../../../graphql/types";
import {
  clearCompletedMemberApplicationOperation,
  getOrCreateMemberApplicationOperation,
  MemberApplicationOperationStorage,
} from "../Members/memberApplicationOperation";

type InvoiceCreateOperationInput = Omit<
  CreateCmpInvoiceInput,
  "operationId"
> & {
  operationId?: string;
};

type InvoiceCreateInputWithOperation = CreateCmpInvoiceInput & {
  operationId: string;
};

export type PrepareCmpInvoiceCreateOperationInput = {
  createOperationId?: () => string;
  fingerprintRequest?: (request: unknown) => Promise<string>;
  input: InvoiceCreateOperationInput;
  storage?: MemberApplicationOperationStorage;
};

export type PreparedCmpInvoiceCreateOperation = {
  input: InvoiceCreateInputWithOperation;
  operationId: string;
  storageKey: string;
};

export type RunCmpInvoiceCreateOperationInput<MutationResult> =
  PrepareCmpInvoiceCreateOperationInput & {
    execute: (input: InvoiceCreateInputWithOperation) => Promise<MutationResult>;
    isAuthoritativelySaved: (result: MutationResult) => boolean;
  };

export const getCmpInvoiceCreateOperationStorageKey = ({
  chamberId,
  replacesInvoiceId,
}: {
  chamberId: string;
  replacesInvoiceId?: string | null;
}) => {
  const normalizedChamberId = chamberId.trim();
  const normalizedOriginalInvoiceId = replacesInvoiceId?.trim();

  return normalizedOriginalInvoiceId
    ? `cmp-invoice-replacement-operation:v1:${encodeURIComponent(
        normalizedChamberId
      )}:${encodeURIComponent(normalizedOriginalInvoiceId)}`
    : `cmp-invoice-create-operation:v1:${encodeURIComponent(
        normalizedChamberId
      )}`;
};

/**
 * Persists only a request fingerprint and opaque UUID. A retry of the exact
 * normalized GraphQL input reuses its operation, while any request change gets
 * a new UUID. Replacement slots are isolated by their original invoice.
 */
export const prepareCmpInvoiceCreateOperation = async ({
  createOperationId,
  fingerprintRequest,
  input,
  storage,
}: PrepareCmpInvoiceCreateOperationInput): Promise<PreparedCmpInvoiceCreateOperation> => {
  const { operationId: _untrustedOperationId, ...operationFreeInput } = input;
  const storageKey = getCmpInvoiceCreateOperationStorageKey({
    chamberId: operationFreeInput.chamberId,
    replacesInvoiceId: operationFreeInput.replacesInvoiceId,
  });
  const operation = await getOrCreateMemberApplicationOperation({
    ...(createOperationId ? { createOperationId } : {}),
    ...(fingerprintRequest ? { fingerprintRequest } : {}),
    request: operationFreeInput,
    ...(storage ? { storage } : {}),
    storageKey,
  });

  return {
    input: {
      ...operationFreeInput,
      operationId: operation.operationId,
    },
    operationId: operation.operationId,
    storageKey,
  };
};

/**
 * Clears the retry operation only when the returned GraphQL payload proves
 * that the invoice was saved. Lost and incomplete responses retain the UUID.
 */
export const runCmpInvoiceCreateOperation = async <MutationResult>({
  execute,
  isAuthoritativelySaved,
  ...prepareInput
}: RunCmpInvoiceCreateOperationInput<MutationResult>): Promise<MutationResult> => {
  const prepared = await prepareCmpInvoiceCreateOperation(prepareInput);
  const result = await execute(prepared.input);

  if (isAuthoritativelySaved(result)) {
    clearCompletedMemberApplicationOperation({
      operationId: prepared.operationId,
      ...(prepareInput.storage ? { storage: prepareInput.storage } : {}),
      storageKey: prepared.storageKey,
    });
  }

  return result;
};
